import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchPaymentOptions } from "../../functions/paymentApi";
import "./MethodChoiceModal.css";

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};
const IconClose = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" {...stroke}><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const IconCard = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...stroke}><rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M2 10h20M6 15h4" /></svg>
);
const IconBolt = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...stroke}><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" /></svg>
);
const IconArrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...stroke}><path d="m9 6 6 6-6 6" /></svg>
);
const IconSparkle = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" /></svg>
);

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

const MethodChoiceModal = ({ showModal, handleClose, bookingId, onChoose }) => {
  const [opts, setOpts] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const modalRef = useRef(null);

  const load = useCallback(() => {
    if (!bookingId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPaymentOptions(bookingId)
      .then((data) => { if (!cancelled) { setOpts(data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [bookingId]);

  useEffect(() => {
    if (!showModal) return undefined;
    return load();
  }, [showModal, load]);

  useEffect(() => {
    if (!showModal) return undefined;
    modalRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal, handleClose]);

  if (!showModal) return null;

  const savings = opts ? opts.stripe.amount - opts.etransfer.amount : 0;
  const savingsPct = opts && opts.stripe.amount > 0
    ? Math.round((savings / opts.stripe.amount) * 100)
    : 0;

  return (
    <div className="MC-overlay" onClick={handleClose}>
      <div
        className="MC-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="MC-title"
        tabIndex={-1}
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="MC-close" onClick={handleClose} aria-label="Close">
          <IconClose />
        </button>

        <div className="MC-body">
          <div className="MC-head">
            <span className="MC-eyebrow">Payment</span>
            <h2 className="MC-title" id="MC-title">Choose how to pay</h2>
            <p className="MC-sub">Same booking, two ways. Pick what works best for you.</p>
          </div>

          {loading && (
            <div className="MC-loading" aria-live="polite" aria-busy="true">
              <div className="MC-skeleton" />
              <div className="MC-skeleton" />
            </div>
          )}

          {error && (
            <div className="MC-error" role="alert">
              <div className="MC-error-card">
                <p className="MC-error-title">Something went wrong</p>
                <p className="MC-error-detail">{error}</p>
                <button type="button" className="MC-retry" onClick={load}>Try again</button>
              </div>
            </div>
          )}

          {opts && !loading && !error && (
            <>
              {savings > 0 && (
                <div className="MC-savings">
                  <IconSparkle />
                  <span>
                    Save <strong>{fmt(savings)}</strong> ({savingsPct}%) — no card processing fee with the option below.
                  </span>
                </div>
              )}

              <div className="MC-options">
                <button
                  type="button"
                  className="MC-option is-recommended"
                  onClick={() => onChoose("etransfer", opts)}
                >
                  <span className="MC-option-icon"><IconBolt /></span>
                  <span className="MC-option-main">
                    <span className="MC-option-name">
                      Interac e-Transfer
                      <span className="MC-pill">Recommended</span>
                    </span>
                    <span className="MC-option-sub">Sent from your bank. No processing fee.</span>
                  </span>
                  <span className="MC-option-totals">
                    <span className="MC-option-total">{fmt(opts.etransfer.amount)}</span>
                    <span className="MC-option-fee">Service total {fmt(opts.baseAmount)}</span>
                  </span>
                  <span className="MC-option-arrow" aria-hidden="true"><IconArrow /></span>
                </button>

                <button
                  type="button"
                  className="MC-option"
                  onClick={() => onChoose("card", opts)}
                >
                  <span className="MC-option-icon"><IconCard /></span>
                  <span className="MC-option-main">
                    <span className="MC-option-name">Credit / Debit Card</span>
                    <span className="MC-option-sub">Instant, via Stripe. {opts.stripe.note}</span>
                  </span>
                  <span className="MC-option-totals">
                    <span className="MC-option-total">{fmt(opts.stripe.amount)}</span>
                    <span className="MC-option-fee">
                      {fmt(opts.baseAmount)} <span className="MC-fee-add">+ {fmt(opts.stripe.fee)} fee</span>
                    </span>
                  </span>
                  <span className="MC-option-arrow" aria-hidden="true"><IconArrow /></span>
                </button>
              </div>

              <p className="MC-foot-note">
                You can cancel any time before confirmation.
              </p>
            </>
          )}

          <div className="MC-footer">
            <button type="button" className="MC-btn-ghost" onClick={handleClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MethodChoiceModal;
